import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { ReactNode } from "react";

/**
 * The frame shared by /login and /sign-up.
 *
 * Clerk renders the form; this keeps the brand, the card and the failure states
 * identical on both pages. The failure states matter more than they look: a
 * signed-in host lands here whenever the Worker cannot resolve their profile,
 * and showing the sign-in form again would invite a second sign-in that cannot
 * fix a server fault.
 */
export function AuthShell({ children }: { children: ReactNode }) {
  return (
    <div className="auth-shell">
      <Link className="brand" to="/">
        <span className="brand-mark" aria-hidden="true" />
        Eventgram
      </Link>
      {children}
      <p className="auth-support-note">
        Have questions or need help? Contact us at{" "}
        <a href="mailto:support@eventgram.space">support@eventgram.space</a>
      </p>
    </div>
  );
}

export function AuthCard({ children }: { children: ReactNode }) {
  return <div className="card auth-card">{children}</div>;
}

/**
 * A shape where the Clerk form will be.
 *
 * Clerk's widget is a separate script, so on a cold load the card used to sit
 * empty for a second or two: the heading and the intro were there, and the form
 * they were introducing was not. A blank box reads as broken, especially on the
 * one page where a host is already deciding whether to trust the product. This
 * keeps the card honest while the script arrives, and tells a screen reader that
 * work is still in progress rather than reading an empty region.
 */
export function AuthSkeleton({ label }: { label: string }) {
  return (
    <div className="auth-skeleton" role="status" aria-live="polite" aria-busy="true" aria-label={label}>
      <div className="auth-skeleton-social" aria-hidden="true" />
      <div className="auth-skeleton-divider" aria-hidden="true">
        <span />
        <em>or</em>
        <span />
      </div>
      <div className="auth-skeleton-field" aria-hidden="true" />
      <div className="auth-skeleton-field" aria-hidden="true" />
      <div className="auth-skeleton-button" aria-hidden="true" />
      <p className="auth-skeleton-note">{label}</p>
    </div>
  );
}

/**
 * Wraps Clerk's widget with a skeleton until Clerk's DOM elements actually mount.
 * This completely eliminates the 1-2 second blank white/transparent box while
 * Clerk's client script hydrates and renders the form fields.
 */
export function AuthWidgetSlot({
  children,
  label,
}: {
  children: ReactNode;
  label: string;
}) {
  const [ready, setReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const checkReady = () => {
      const renderedChild = el.querySelector(
        ".auth-clerk-card, .cl-card, .cl-formButtonPrimary, form, input"
      );
      if (renderedChild) {
        setReady(true);
      }
    };

    checkReady();
    const observer = new MutationObserver(() => checkReady());
    observer.observe(el, { childList: true, subtree: true });

    const fallbackTimer = setTimeout(() => setReady(true), 3500);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, []);

  return (
    <div ref={containerRef} className="auth-widget-slot" style={{ position: "relative", minHeight: "330px" }}>
      {!ready && (
        <div
          className="auth-widget-skeleton-wrapper"
          style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 1 }}
        >
          <AuthSkeleton label={label} />
        </div>
      )}
      <div
        className="auth-widget-content"
        style={{
          opacity: ready ? 1 : 0,
          transition: "opacity 0.25s ease-in-out",
          position: "relative",
          zIndex: 2,
        }}
      >
        {children}
      </div>
    </div>
  );
}

export function AuthProblem({
  eyebrow,
  title,
  message,
  onRetry,
}: {
  eyebrow: string;
  title: string;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <AuthCard>
      <div style={{ textAlign: "center" }}>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p className="muted">{message}</p>
        <div className="stack" style={{ gap: "0.75rem" }}>
          {onRetry ? (
            <button className="button" type="button" onClick={onRetry}>
              Try again
            </button>
          ) : null}
          <Link className="button button-secondary" to="/">
            Back to home
          </Link>
        </div>
      </div>
    </AuthCard>
  );
}
